import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { User, Transaction } from '../App';
import { toast } from 'sonner';
import { CreditCard, Copy, Gift, Users, TrendingUp, ArrowUpRight, ArrowDownLeft, Wallet, Settings, X, Trophy } from 'lucide-react';
import { ShareModal } from './ShareModal';
import { NewsSlider } from './NewsSlider';
import { TransactionDetailsModal } from './TransactionDetailsModal';
import { WelcomePopup } from './WelcomePopup';
import { BoomEventPopup } from './BoomEventPopup';
import { EventEndedPopup } from './EventEndedPopup';
import { WinnerCheckPopup } from './WinnerCheckPopup';
import { EventEndedNoticePopup } from './EventEndedNoticePopup';
import { getBrandName } from '../utils/brand';
import { i18n, tb } from '../utils/i18n';

interface DashboardProps {
  user: User;
  transactions: Transaction[];
  onUpdateBalance: (newBalance: number) => void;
  onAddTransaction: (transaction: Transaction) => void;
  onNavigateToNews?: () => void;
  onNavigateToSettings?: () => void;
  onNavigateToEvents?: () => void;
}

export function Dashboard({ user, transactions, onUpdateBalance, onAddTransaction, onNavigateToNews, onNavigateToSettings, onNavigateToEvents }: DashboardProps) {
  // user가 없으면 로딩 화면 표시
  if (!user) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>{i18n.t('loadingUserInfo')}</p>
        </div>
      </div>
    );
  }

  const [showShareModal, setShowShareModal] = useState(false);
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [showWelcomeBonus, setShowWelcomeBonus] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [showReferralBanner, setShowReferralBanner] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [showBoomEventPopup, setShowBoomEventPopup] = useState(false);
  const [showEventEndedPopup, setShowEventEndedPopup] = useState(false);
  const [showEventEndedNoticePopup, setShowEventEndedNoticePopup] = useState(false);
  const [showWinnerCheckPopup, setShowWinnerCheckPopup] = useState(false);
  const [winnerInfo, setWinnerInfo] = useState<{
    rank: number | null;
    prizeAmount: number | null;
    hasParticipated: boolean;
  }>({
    rank: null,
    prizeAmount: null,
    hasParticipated: false
  });

  // 시간 기반 인사말 함수
  const getGreeting = () => {
    const hour = new Date().getHours();
    
    if (hour >= 6 && hour < 12) {
      return 'Good morning';
    } else if (hour >= 12 && hour < 18) {
      const afternoonGreetings = [
        'Good afternoon',
        'Have a great day!',
        'Enjoy your day!',
        'Have a wonderful day!',
        'Make it a great day!',
        'Have an amazing day!'
      ];
      return afternoonGreetings[Math.floor(Math.random() * afternoonGreetings.length)];
    } else {
      const eveningGreetings = [
        'Good evening',
        'Have a great evening!',
        'Enjoy your evening!',
        'Have a wonderful evening!',
        'Make it a great evening!',
        'Have an amazing evening!'
      ];
      return eveningGreetings[Math.floor(Math.random() * eveningGreetings.length)];
    }
  };



  useEffect(() => {
    if (!user?.id) return;
    
    // 🔥 Boom Event 팝업 표시 (로그인 시 자동 표시, "다시 보지 않기" 체크 시에만 숨김)
    // ✅ 2차 붐 이벤트 종료 시간(2025-11-20 16:00) 이후에는 팝업 표시 안 함
    const eventEndTime = new Date('2025-11-20T16:00:00').getTime();
    const now = Date.now();
    const isEventEnded = now >= eventEndTime;
    
    // 2차 붐 이벤트 팝업 (종료 전에만 표시)
    const hasSeenBoomPopup = localStorage.getItem('vatton-boom-popup-dismissed');
    if (!hasSeenBoomPopup && !isEventEnded) {
      // 로그인 시 바로 표시 (이벤트 종료 전에만)
      setTimeout(() => {
        setShowBoomEventPopup(true);
      }, 1000);
    }
    
    // ✅ 2차 붐 이벤트 종료 안내 팝업 (2025-11-20 16:00 이후에만 표시)
    if (isEventEnded) {
      const hasSeenNotice = localStorage.getItem('vatton-2nd-event-ended-notice-seen');
      if (!hasSeenNotice) {
        // 로그인 시 1.5초 후 표시
        setTimeout(() => {
          setShowEventEndedNoticePopup(true);
        }, 1500);
      }
    }
    
    // 웰컴배너는 항상 표시
    setShowReferralBanner(true);
  }, [user?.id]);

  const handleTransactionClick = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowTransactionDetails(true);
  };

  const handleWelcomeAirdrop = async () => {
    try {
      const token = localStorage.getItem('vatton-jwt-token');
      if (!token) {
        toast.error(i18n.t('authRequired'));
        return;
      }

      const response = await fetch('http://localhost:3600/api/vatton/welcome-bonus/claim', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '에어드롭 처리 실패');
      }

      const result = await response.json();
      
      // 잔고 업데이트
      await onUpdateBalance(user.balance + result.amount);
      
      // 트랜잭션 추가
      const newTransaction = {
        type: 'Welcome Airdrop',
        amount: result.amount,
        createdAt: new Date().toISOString()
      };
      await onAddTransaction(newTransaction);
      
      toast.success(`Welcome airdrop ${result.amount} VTTN received!`);
    } catch (error) {
      console.error('에어드롭 처리 오류:', error);
      toast.error('Error processing airdrop.');
    }
  };

  const handleReferralCode = () => {
    setShowReferralModal(true);
  };

  // 웰컴 팝업 관련 함수들
  const handleWelcomeBonusClick = () => {
    setShowWelcomePopup(false);
    setShowWelcomeBonus(true);
  };

  const handleDontShowAgain = () => {
    localStorage.setItem('vatton-welcome-popup-seen', 'true');
    setShowWelcomePopup(false);
  };

  const handleWelcomePopupClose = () => {
    setShowWelcomePopup(false);
  };

  const handleBoomEventPopupClose = () => {
    setShowBoomEventPopup(false);
  };

  const handleEventEndedPopupClose = () => {
    localStorage.setItem('vatton-event-ended-seen', 'true');
    setShowEventEndedPopup(false);
  };

  const handleEventEndedNoticePopupClose = () => {
    setShowEventEndedNoticePopup(false);
  };

  const handleEventEndedNoticePopupDontShow = () => {
    localStorage.setItem('vatton-2nd-event-ended-notice-seen', 'true');
    setShowEventEndedNoticePopup(false);
  };

  const handleBoomEventPopupParticipate = () => {
    setShowBoomEventPopup(false);
    // Events 페이지로 이동하고 events 탭 활성화
    if (onNavigateToEvents) {
      // hash를 먼저 제거하여 events 탭이 활성화되도록 함
      if (window.location.hash === '#news') {
        window.location.hash = '';
      }
      onNavigateToEvents();
    }
  };

  const handleBoomEventPopupDontShow = () => {
    localStorage.setItem('vatton-boom-popup-dismissed', 'true');
    setShowBoomEventPopup(false);
  };

  const handleCheckWinner = async () => {
    try {
      const token = localStorage.getItem('vatton-jwt-token');
      if (!token) {
        toast.error(i18n.t('loginRequired'));
        return;
      }

      const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3600';
      const response = await fetch(`${API_URL}/api/boom/check-winner/1`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('당첨 조회 응답 (수동):', data);
        
        if (data.success) {
          const hasParticipated =
            typeof data.hasParticipated === 'boolean'
              ? data.hasParticipated
              : Boolean(data.rank ?? data.prizeAmount);

          setWinnerInfo({
            rank: data.rank ?? null,
            prizeAmount: data.prizeAmount ?? null,
            hasParticipated: hasParticipated
          });
          
          // 배너 클릭 시에는 참여 여부와 관계없이 팝업 표시 (모든 사용자가 확인 가능)
          setShowWinnerCheckPopup(true);
        }
      } else {
        toast.error(i18n.t('winnerCheckFailed'));
      }
    } catch (error) {
      console.error('당첨 조회 오류:', error);
      toast.error(i18n.t('winnerCheckError'));
    }
  };

  const handleReferralSubmit = async () => {
    if (!referralCode.trim()) {
      toast.error('Please enter a referral code.');
      return;
    }

    if (isVerifying) {
      return; // 이미 인증 중이면 무시
    }

    setIsVerifying(true);

    try {
      const token = localStorage.getItem('vatton-jwt-token');
      if (!token || !user?.id) {
        toast.error(i18n.t('authRequired'));
        setIsVerifying(false);
        return;
      }

      // 1. 추천코드 검증
      const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3600';
      const validateResponse = await fetch(`${API_URL}/api/referrals/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ referralCode: referralCode.trim().toUpperCase() })
      });

      console.log('Validate response status:', validateResponse.status);
      console.log('Validate response headers:', validateResponse.headers);

      if (!validateResponse.ok) {
        const responseText = await validateResponse.text();
        console.log('Error response:', responseText);
        try {
          const errorData = JSON.parse(responseText);
          throw new Error(errorData.error || '추천코드 검증 실패');
        } catch (parseError) {
          throw new Error(`서버 오류: ${validateResponse.status} - ${responseText}`);
        }
      }

      const responseText = await validateResponse.text();
      console.log('Success response:', responseText);
      const validateResult = JSON.parse(responseText);
      
      if (!validateResult.valid) {
        toast.error('Invalid referral code.');
        setIsVerifying(false);
        return;
      }

      // 2. 웰컴 보너스 실제 지급 (블록체인 전송)
      const applyResponse = await fetch(`${API_URL}/api/referrals/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          userId: user.id, 
          referralCode: referralCode.trim().toUpperCase() 
        })
      });

      if (!applyResponse.ok) {
        const responseText = await applyResponse.text();
        console.log('Apply error response:', responseText);
        try {
          const errorData = JSON.parse(responseText);
          throw new Error(errorData.error || '웰컴 보너스 지급 실패');
        } catch (parseError) {
          throw new Error(`서버 오류: ${applyResponse.status} - ${responseText}`);
        }
      }

      const applyResponseText = await applyResponse.text();
      console.log('Apply success response:', applyResponseText);
      const applyResult = JSON.parse(applyResponseText);
      
      // 3. 모달 전환 및 UI 업데이트
      setShowReferralModal(false);
      setShowWelcomeBonus(true);
      setReferralCode('');
      
      // 4. 사용자 잔액 새로고침 (블록체인에서 조회)
      setTimeout(async () => {
        try {
          const balanceRes = await fetch(`${API_URL}/api/blockchain/vttn/balance/${user.walletAddress}`);
          if (balanceRes.ok) {
            const balanceData = await balanceRes.json();
            user.balance = parseFloat(balanceData.balance);
          }
          window.location.reload(); // 전체 페이지 새로고침으로 모든 컴포넌트 업데이트
        } catch (err) {
          console.warn('잔액 새로고침 실패:', err);
        }
      }, 2000);
      
      toast.success(`Welcome Bonus 100 VTTN received! TX: ${applyResult.txHash?.slice(0, 10)}...`);
      
    } catch (error: any) {
      console.error('추천코드 처리 오류:', error);
      
      // 이미 받은 경우 특별 처리
      if (error.message?.includes('이미 추천코드를 적용했습니다') || 
          error.message?.includes('이미 웰컴 보너스를 수령했습니다')) {
        toast.info(i18n.t('alreadyReceivedBonus'));
      } else {
        toast.error(error.message || 'Error processing referral code.');
      }
    } finally {
      setIsVerifying(false);
    }
  };



  const handleCloseBonusModal = () => {
    setShowWelcomeBonus(false);
    if (dontShowAgain) {
      localStorage.setItem('vatton-referral-banner-hidden', 'true');
      setShowReferralBanner(false);
    }
  };

  const hideBanner = () => {
    localStorage.setItem('vatton-referral-banner-hidden', 'true');
    setShowReferralBanner(false);
  };

  const copyWalletAddress = () => {
    if (!user?.walletAddress) {
      toast.error(i18n.t('walletAddressNotFound'));
      return;
    }

    // HTTP 환경에서도 작동하는 방식 (execCommand 우선)
    const textArea = document.createElement('textarea');
    textArea.value = user.walletAddress;
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        toast.success(i18n.t('addressCopied'));
      } else {
        toast.error(i18n.t('addressCopyFailed'));
      }
    } catch (err) {
      console.error('복사 오류:', err);
      toast.error(i18n.t('addressCopyFailed'));
    }

    document.body.removeChild(textArea);
  };

  const copyReferralCode = async () => {
    try {
      await navigator.clipboard.writeText(user.referralCode);
      toast.success('Referral code copied!');
    } catch (error) {
      toast.error('Failed to copy code.');
    }
  };

  return (
    <div className="h-full bg-background flex flex-col">
      <div className="flex-1 overflow-auto scrollbar-hide p-4 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <div>
            <h1 className="text-2xl font-light text-foreground">{getGreeting()}</h1>
            <p className="text-muted-foreground">{user.nickname}</p>
          </div>
          <div className="flex items-center space-x-3">
            {/* Settings Icon */}
            <button
              onClick={onNavigateToSettings}
              className="w-10 h-10 bg-muted rounded-full flex items-center justify-center hover:bg-accent transition-colors"
            >
              <Settings className="h-5 w-5 text-muted-foreground" />
            </button>
            
            {/* Referral Code Display */}
            <div className="text-right">
              <p className="text-muted-foreground text-xs">Referral Code</p>
              <p className="text-foreground font-bold text-sm">{user.referralCode}</p>
            </div>
            
            {/* Profile Avatar - Clickable */}
            <button
              onClick={() => setShowShareModal(true)}
              className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg hover:scale-105 transition-transform"
            >
              {user.nickname.charAt(0).toUpperCase()}
            </button>
          </div>
        </div>

        {/* Modern Card */}
        <Card className={`border-0 text-white relative overflow-hidden ${
          user.walletAddress === '0xVATTONFOUNDATION000000000000000A888888' 
            ? 'bg-gradient-to-br from-green-600 to-emerald-500' 
            : 'bg-gradient-to-br from-blue-600 to-blue-500'
        }`}>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-8">
              <div>
                <div className="flex items-center space-x-2 mb-1">
                  <p className="text-white/80 text-sm">Total Balance</p>
                  {user.walletAddress === '0xVATTONFOUNDATION000000000000000A888888' && (
                    <span className="bg-white/20 text-white text-xs px-2 py-1 rounded-full font-medium">
                      🏛️ Foundation
                    </span>
                  )}
                </div>
                <p className="text-3xl font-light">{user.balance ? user.balance.toFixed(2) : '0.00'}</p>
                <p className="text-white/60 text-sm">VTTN</p>
              </div>
              <div className="flex flex-col items-end space-y-2">
                <Wallet className="h-8 w-8 text-white/60" />
                {user.walletAddress === '0xVATTONFOUNDATION000000000000000A888888' && (
                  <div className="text-right">
                    <p className="text-white/80 text-xs">Vatton Foundation</p>
                    <p className="text-white/60 text-xs">Official Wallet</p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="space-y-3">
              <div>
                <p className="text-white/60 text-xs mb-1">Wallet Address</p>
                <div className="flex items-center justify-between">
                  <p className="text-white/90 text-sm font-mono">
                    {user.walletAddress.slice(0, 20)}...
                  </p>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={copyWalletAddress}
                    className="text-white/80 hover:text-white hover:bg-white/10 p-1 h-auto"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-xs">Member since</p>
                  <p className="text-white/90 text-sm">
                    {new Date(user.createdAt).toLocaleDateString('en-US', { 
                      month: 'short', 
                      year: 'numeric' 
                    })}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-white font-medium">{getBrandName()}</p>
                  <p className="text-white/60 text-xs">Digital Wallet</p>
                </div>
              </div>
            </div>
            
            {/* Card decorative elements */}
            <div className="absolute top-4 right-4 w-12 h-8 bg-white/20 rounded"></div>
            <div className="absolute bottom-4 right-4 flex space-x-1">
              <div className="w-2 h-2 bg-white/40 rounded-full"></div>
              <div className="w-2 h-2 bg-white/40 rounded-full"></div>
            </div>
          </CardContent>
        </Card>

        {/* Welcome Bonus Banner */}
        {showReferralBanner && (
          <Card 
            className="bg-gradient-to-r from-green-500 to-emerald-500 border-0 text-white cursor-pointer hover:from-green-600 hover:to-emerald-600 transition-all duration-300"
            onClick={handleReferralCode}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <Gift className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-bold">{tb('welcomeBannerTitle')}</p>
                  <p className="text-white/80 text-sm">{tb('welcomeBannerDesc')}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReferralCode();
                    }}
                    className="bg-white text-green-600 hover:bg-white/90 h-9 px-4 text-sm font-bold"
                  >
                    {i18n.t('getBonus')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Push Notification Cards */}
        <div className="space-y-4">
          {/* BOOM Event Notification */}
          <Card 
            className="bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 border-orange-200 dark:border-red-800 cursor-pointer hover:from-orange-100 hover:to-red-100 transition-all duration-300"
            onClick={onNavigateToEvents}
          >
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                  <Trophy className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-orange-800 dark:text-orange-300">{tb('boom2EndedBannerTitle')}</p>
                  <p className="text-orange-600 dark:text-orange-400 text-sm">{tb('boom2EndedBannerDesc')}</p>
                </div>
                <div className="text-right">
                  <p className="text-orange-600 font-bold">{i18n.t('totalPrizeLabel')}</p>
                  <p className="text-muted-foreground text-xs">180,000 VTTN</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 1차 당첨자 조회 배너 (전체 유저 공개) */}
          {/* 테스트 계정 조건: (user.nickname === '3bebe' || user.nickname === '4bebe' || user.nickname === '5bebe') */}
          <Card 
            className="bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 border-yellow-200 dark:border-yellow-800 cursor-pointer hover:from-yellow-100 hover:to-amber-100 transition-all duration-300"
            onClick={handleCheckWinner}
          >
            <CardContent className="p-4">
              <div className="flex items-center space-between gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-yellow-500 to-amber-500 rounded-full flex items-center justify-center">
                  <Trophy className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-yellow-800 dark:text-yellow-300">{tb('boom1WinnerBannerTitle')}</p>
                  <p className="text-yellow-600 dark:text-yellow-400 text-sm">{tb('boom1WinnerBannerDesc')}</p>
                </div>
                <div className="text-right">
                  <p className="text-yellow-600 font-bold text-sm">{i18n.t('clickToCheckLabel')}</p>
                  <p className="text-muted-foreground text-xs">{i18n.t('checkResultLabel')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* News Slider */}
        <NewsSlider onNavigateToNews={onNavigateToNews} />

        {/* Recent Activity - 주석처리 (TX 문제 해결 전까지) */}
        {/* <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-foreground font-medium">Recent Transactions</p>
                <p className="text-muted-foreground text-sm">{transactions.length} total</p>
              </div>
            </div>
            
            {transactions.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Wallet className="h-8 w-8 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground">No transactions yet</p>
                <p className="text-muted-foreground/60 text-sm mt-1">Start your first transaction</p>
              </div>
            ) : (
              <div className="space-y-3">
                {transactions.slice(0, 5).map((tx, index) => (
                  <button
                    key={tx.hash || tx.id || index}
                    onClick={() => handleTransactionClick(tx)}
                    className="w-full flex items-center justify-between p-3 bg-muted rounded-xl hover:bg-accent transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        tx.amount > 0 ? 'bg-green-500/20' : 'bg-red-500/20'
                      }`}>
                        {tx.amount > 0 ? (
                          <ArrowDownLeft className="h-5 w-5 text-green-600" />
                        ) : (
                          <ArrowUpRight className="h-5 w-5 text-red-600" />
                        )}
                      </div>
                      <div className="text-left">
                        <p className="text-foreground font-medium">{tx.type}</p>
                        <p className="text-muted-foreground text-sm">
                          {tx.timestamp ? new Date(tx.timestamp * 1000).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : tx.createdAt ? new Date(tx.createdAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : 'Invalid Date'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${
                        tx.amount > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {tx.amount > 0 ? '+' : ''}{parseFloat(tx.amount).toFixed(2)} VTTN
                      </p>
                      <p className="text-muted-foreground text-xs">
                        ≈ ${tx.amount ? Math.abs(tx.amount * 0.5).toFixed(2) : '0.00'}
                      </p>
                    </div>
                  </button>
                ))}
                
                {transactions.length > 5 && (
                  <button className="w-full py-3 text-primary hover:bg-primary/5 rounded-xl transition-colors">
                    View All Transactions
                  </button>
                )}
              </div>
            )}
          </CardContent>
        </Card> */}
      </div>

      {/* Share Modal */}
      <ShareModal 
        isOpen={showShareModal} 
        onClose={() => setShowShareModal(false)} 
        user={user} 
      />

      {/* Transaction Details Modal */}
      <TransactionDetailsModal
        isOpen={showTransactionDetails}
        onClose={() => setShowTransactionDetails(false)}
        transaction={selectedTransaction}
      />

      {/* Welcome Popup */}
      <WelcomePopup
        isOpen={showWelcomePopup}
        onClose={handleWelcomePopupClose}
        onWelcomeBonusClick={handleWelcomeBonusClick}
        onDontShowAgain={handleDontShowAgain}
      />

      {/* Boom Event Popup */}
      <BoomEventPopup
        isOpen={showBoomEventPopup}
        onClose={handleBoomEventPopupClose}
        onParticipate={handleBoomEventPopupParticipate}
        onDontShowAgain={handleBoomEventPopupDontShow}
        onNavigateToNews={onNavigateToEvents ? () => {
          onNavigateToEvents();
          // Events 페이지에서 News 탭으로 이동
          setTimeout(() => {
            window.location.hash = '#news';
          }, 100);
        } : undefined}
        onWelcomeBonusClick={handleReferralCode}
      />

      {/* Event Ended Popup */}
      <EventEndedPopup
        isOpen={showEventEndedPopup}
        onClose={handleEventEndedPopupClose}
      />

      {/* 2차 붐 이벤트 종료 안내 팝업 */}
      <EventEndedNoticePopup
        isOpen={showEventEndedNoticePopup}
        onClose={handleEventEndedNoticePopupClose}
        onDontShowAgain={handleEventEndedNoticePopupDontShow}
      />

      {/* Winner Check Popup */}
      <WinnerCheckPopup
        isOpen={showWinnerCheckPopup}
        onClose={() => {
          setShowWinnerCheckPopup(false);
        }}
        nickname={user?.nickname || ''}
        rank={winnerInfo.rank}
        prizeAmount={winnerInfo.prizeAmount}
        hasParticipated={winnerInfo.hasParticipated}
        onViewAllWinners={() => {
          if (onNavigateToEvents) {
            onNavigateToEvents();
            // News 탭으로 이동하기 위해 hash 설정
            setTimeout(() => {
              window.location.hash = '#news';
            }, 100);
          }
        }}
        onParticipate={handleBoomEventPopupParticipate}
        onDontShowAgain={() => {
          localStorage.setItem('vatton-winner-check-popup-seen', 'true');
        }}
      />

      {/* Referral Code Modal */}
      {showReferralModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Gift className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">Welcome Bonus</h3>
              <p className="text-muted-foreground text-sm">Enter referral code to get 100 VTTN</p>
            </div>

            <div className="space-y-4">
              <div>
                <Input
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  placeholder="Enter referral code"
                  className="h-12 text-lg font-semibold border-2 focus:border-green-500 text-center uppercase tracking-wider"
                  autoFocus
                />
              </div>
              
              <div className="flex space-x-3">
                <Button
                  onClick={() => setShowReferralModal(false)}
                  variant="outline"
                  className="flex-1 h-12 text-base font-semibold"
                  disabled={isVerifying}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleReferralSubmit}
                  disabled={!referralCode.trim() || isVerifying}
                  className="flex-1 h-12 bg-green-600 hover:bg-green-700 text-white text-base font-semibold"
                >
                  {isVerifying ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Verifying...</span>
                    </div>
                  ) : (
                    'Verify'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}



      {/* Welcome Bonus Success Modal */}
      {showWelcomeBonus && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Gift className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">Congratulations!</h3>
              <p className="text-muted-foreground text-sm mb-4">Welcome bonus verified successfully</p>
              
              <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 mb-4">
                <p className="text-green-600 font-bold text-2xl">+100 VTTN</p>
                <p className="text-green-600/80 text-sm">has been credited to your wallet</p>
              </div>
            </div>

            <div className="space-y-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm text-muted-foreground">Don't show again</span>
              </label>
              
              <Button
                onClick={handleCloseBonusModal}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}